/**
 * Typed HTTP client for the NestJS API.
 *
 * Default base is the RELATIVE `/api` prefix: in every deployment the ingress
 * routes `/api/*` to the api service and strips the prefix, so the browser
 * talks to the same origin and no API host is baked into the bundle. `VITE_API_URL`
 * is only set for unusual contours — Vite's dev server proxies `/api` to :3001
 * (see vite.config.ts).
 */

import type { AuthMe, HealthResponse } from '@ismart/shared';
import type {
  AdminsView,
  AlertsView,
  AuditView,
  CommandStatusView,
  HealthView,
  JobDetailView,
  JobView,
  MetricsView,
  OverviewView,
  RunDetailView,
  RunNowView,
  UsageSample,
} from './types';


export type { AuthMe, HealthResponse };

// `import.meta.env` only exists under Vite; reading it defensively keeps this
// module importable from the node-side unit tests (renderToStaticMarkup).
const viteEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;

export const API_BASE_URL = viteEnv?.VITE_API_URL ?? '/api';

/** An API error with its HTTP status and the NestJS message/body. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly method: string,
    readonly path: string,
    message: string,
  ) {
    super(`${method} ${path} failed with HTTP ${status}: ${message}`);
    this.name = 'ApiError';
  }
}

/** Anything serialisable into a query string; `undefined`/`null`/'' drop the key. */
export type QueryValue = string | number | boolean | null | undefined;

export function buildQuery(params: object = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, QueryValue>)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

async function parseError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: unknown };
    if (Array.isArray(body.message)) return body.message.join('; ');
    if (typeof body.message === 'string') return body.message;
    return JSON.stringify(body);
  } catch {
    return response.statusText || 'unknown error';
  }
}

/**
 * A 401 means the session is gone. The browser is sent to the OAuth entry
 * point with a FULL page load — the session cookie is set by the api on a
 * redirect chain that starts at oauth.yandex.ru, and a client-side route change
 * would leave the already-mounted queries holding a stale 401.
 *
 * `/auth/me` is excluded: it is the probe that *decides* whether we are logged
 * in, so it must be allowed to answer "no" without bouncing the app.
 */
function handleUnauthorized(path: string): void {
  if (path.startsWith('/auth')) return;
  window.location.assign(`${window.location.origin}${API_BASE_URL}/auth/login`);
}

async function request<T>(method: string, path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: { Accept: 'application/json', ...(init.headers ?? {}) },
  });
  if (!response.ok) {
    if (response.status === 401) handleUnauthorized(path);
    throw new ApiError(response.status, method, path, await parseError(response));
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const health = {
  /** GET /healthz */
  get: (signal?: AbortSignal) => request<HealthResponse>('GET', '/healthz', { signal }),
  /** GET /api/health — the rich document the /health page renders. */
  rich: (signal?: AbortSignal) => request<HealthView>('GET', '/health', { signal }),
};

const id = (value: string): string => encodeURIComponent(value);

export const overview = {
  get: (signal?: AbortSignal) => request<OverviewView>('GET', '/overview', { signal }),
};

export const metrics = {
  get: (signal?: AbortSignal) => request<MetricsView>('GET', '/metrics', { signal }),
  /** Last 14 daily usage samples of one bucket (404 before the first sample). */
  usageTrend: (bucket: string, signal?: AbortSignal) =>
    request<{ bucket: string; samples: UsageSample[] }>('GET', `/metrics/${id(bucket)}/trend`, { signal }),
};

export const jobs = {
  list: (signal?: AbortSignal) => request<JobView[]>('GET', '/jobs', { signal }),
  get: (jobId: string, signal?: AbortSignal) => request<JobDetailView>('GET', `/jobs/${id(jobId)}`, { signal }),
  run: (jobId: string, run: string, signal?: AbortSignal) =>
    request<RunDetailView>('GET', `/jobs/${id(jobId)}/runs/${id(run)}`, { signal }),
  /** Manual run; 403 while the feature flag is off (the shipped state). */
  runNow: (jobId: string) =>
    request<RunNowView>('POST', `/jobs/${id(jobId)}/run`, { method: 'POST', body: '{}' }),
  command: (jobId: string, commandId: string, signal?: AbortSignal) =>
    request<CommandStatusView>('GET', `/jobs/${id(jobId)}/run/${id(commandId)}`, { signal }),
};

export const alerts = {
  get: (signal?: AbortSignal) => request<AlertsView>('GET', '/alerts', { signal }),
};

export const audit = {
  get: (signal?: AbortSignal) => request<AuditView>('GET', '/audit', { signal }),
};

export const admins = {
  list: (signal?: AbortSignal) => request<AdminsView>('GET', '/admins', { signal }),
  add: (email: string) =>
    request<AdminsView>('POST', '/admins', { method: 'POST', body: JSON.stringify({ email }) }),
  remove: (email: string) => request<AdminsView>('DELETE', `/admins/${id(email)}`, { method: 'DELETE' }),
};

/**
 * Session identity. A 401 here is meaningful — the caller decides what to do
 * (the layout shows the login state, it does not redirect).
 */
export const auth = {
  me: (signal?: AbortSignal) => request<AuthMe>('GET', '/auth/me', { signal }),
  /**
   * Full-page navigation: the api destroys the session, then redirects to the
   * `/logged-out` interstitial (never to /auth/login — see LLM_CONTEXT/02-auth.md,
   * gotcha #4). `<a href>` would work too; assign() keeps it one line.
   */
  logout: () => {
    window.location.assign(`${API_BASE_URL}/auth/logout`);
  },
  /** Where the "Sign in" button on the interstitial goes. */
  loginUrl: `${API_BASE_URL}/auth/login`,
};
